import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./UserPanel/Sidebar";

const EMPTY_FORM = {
  id: null,
  group: "",
  access_point: "",
  schedule: "",
  is_allowed: true,
  valid_from: "",
  valid_until: "",
  excluded_person_ids: [],
};

function getApiMessage(data, fallback) {
  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.detail === "string") {
    return data.detail;
  }

  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat(Infinity).join(" ");
  }

  return fallback;
}

function formatDate(value) {
  if (!value) {
    return "Sin límite";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return "Sin límite";
  }

  return new Intl.DateTimeFormat("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export default function GroupPermissions() {
  const { fetchUser } = useAuth();

  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [accessPoints, setAccessPoints] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [members, setMembers] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [detailPermission, setDetailPermission] = useState(null);
  const [deletePermission, setDeletePermission] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showFeedback = useCallback((severity, message) => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  }, []);

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [
        organizationResponse,
        permissionsResponse,
        groupsResponse,
        accessPointsResponse,
        schedulesResponse,
      ] = await Promise.all([
        fetchUser("/tenants/api/logo/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/group-permissions/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/groups/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/access-points/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/schedules/", {
          method: "GET",
          cache: "no-store",
        }),
      ]);

      const organizationData = await readJson(organizationResponse);
      const permissionsData = await readJson(permissionsResponse);
      const groupsData = await readJson(groupsResponse);
      const accessPointsData = await readJson(accessPointsResponse);
      const schedulesData = await readJson(schedulesResponse);

      if (
        !organizationResponse.ok ||
        organizationData.ok !== true ||
        !organizationData.tenant
      ) {
        throw new Error(
          getApiMessage(
            organizationData,
            "No se pudo cargar la organización."
          )
        );
      }

      if (!permissionsResponse.ok || permissionsData.ok !== true) {
        throw new Error(
          getApiMessage(
            permissionsData,
            "No se pudieron cargar los permisos."
          )
        );
      }

      if (!groupsResponse.ok || groupsData.ok !== true) {
        throw new Error(
          getApiMessage(groupsData, "No se pudieron cargar los roles.")
        );
      }

      if (!accessPointsResponse.ok || accessPointsData.ok !== true) {
        throw new Error(
          getApiMessage(
            accessPointsData,
            "No se pudieron cargar los accesos."
          )
        );
      }

      if (!schedulesResponse.ok || schedulesData.ok !== true) {
        throw new Error(
          getApiMessage(
            schedulesData,
            "No se pudieron cargar los horarios."
          )
        );
      }

      setOrganization(organizationData.tenant);

      setPermissions(
        Array.isArray(permissionsData.permissions)
          ? permissionsData.permissions
          : []
      );

      setGroups(
        Array.isArray(groupsData.groups)
          ? groupsData.groups
          : []
      );

      setAccessPoints(
        Array.isArray(accessPointsData.access_points)
          ? accessPointsData.access_points
          : []
      );

      setSchedules(
        Array.isArray(schedulesData.schedules)
          ? schedulesData.schedules
          : []
      );
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo conectar con el servidor."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUser, showFeedback]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadMembers = useCallback(
    async (groupId, permissionId = null) => {
      if (!groupId) {
        setMembers([]);
        return;
      }

      setMembersLoading(true);

      try {
        const query = permissionId
          ? `?permission_id=${permissionId}`
          : "";

        const response = await fetchUser(
          `/access-control/api/web/groups/${groupId}/members/${query}`,
          {
            method: "GET",
            cache: "no-store",
          }
        );

        const data = await readJson(response);

        if (!response.ok || data.ok !== true) {
          throw new Error(
            getApiMessage(
              data,
              "No se pudieron cargar los usuarios del rol."
            )
          );
        }

        const loadedMembers = Array.isArray(data.members)
          ? data.members
          : [];

        setMembers(loadedMembers);

        if (permissionId) {
          setForm((previous) => ({
            ...previous,
            excluded_person_ids: loadedMembers
              .filter((member) => member.is_excluded === true)
              .map((member) => member.id),
          }));
        }
      } catch (error) {
        setMembers([]);

        showFeedback(
          "error",
          error instanceof Error
            ? error.message
            : "No se pudieron cargar los usuarios del rol."
        );
      } finally {
        setMembersLoading(false);
      }
    },
    [fetchUser, showFeedback]
  );

  const filteredPermissions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return permissions;
    }

    return permissions.filter((permission) => {
      return (
        String(permission.group_name || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.access_point_name || "")
          .toLowerCase()
          .includes(query) ||
        String(permission.schedule_name || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [permissions, search]);

  const paginatedPermissions = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredPermissions.slice(
      start,
      start + rowsPerPage
    );
  }, [filteredPermissions, page, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(filteredPermissions.length / rowsPerPage) - 1
    );

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredPermissions.length, page, rowsPerPage]);

  const openCreate = () => {
    setMembers([]);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = async (permission) => {
    setMembers([]);

    setForm({
      id: permission.id,
      group: permission.group ?? "",
      access_point: permission.access_point ?? "",
      schedule: permission.schedule ?? "",
      is_allowed: permission.is_allowed === true,
      valid_from: permission.valid_from || "",
      valid_until: permission.valid_until || "",
      excluded_person_ids: [],
    });

    setFormOpen(true);

    await loadMembers(
      permission.group,
      permission.id
    );
  };

  const closeForm = () => {
    if (saving) {
      return;
    }

    setFormOpen(false);
    setForm(EMPTY_FORM);
    setMembers([]);
  };

  const handleGroupChange = async (event) => {
    const groupId = event.target.value;

    setForm((previous) => ({
      ...previous,
      group: groupId,
      excluded_person_ids: [],
    }));

    await loadMembers(groupId);
  };

  const validateForm = () => {
    if (!form.group) {
      showFeedback("error", "Debes seleccionar un rol.");
      return false;
    }

    if (!form.access_point) {
      showFeedback("error", "Debes seleccionar un acceso.");
      return false;
    }

    if (
      form.valid_from &&
      form.valid_until &&
      form.valid_from > form.valid_until
    ) {
      showFeedback(
        "error",
        "La fecha final no puede ser anterior a la fecha inicial."
      );
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const editing = Boolean(form.id);

      const response = await fetchUser(
        editing
          ? `/access-control/api/web/group-permissions/${form.id}/`
          : "/access-control/api/web/group-permissions/",
        {
          method: editing ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            group: Number(form.group),
            access_point: Number(form.access_point),
            schedule: form.schedule
              ? Number(form.schedule)
              : null,
            is_allowed: form.is_allowed,
            valid_from: form.valid_from || null,
            valid_until: form.valid_until || null,
            excluded_person_ids: form.excluded_person_ids,
          }),
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(
            data,
            editing
              ? "No se pudo actualizar el permiso."
              : "No se pudo crear el permiso."
          )
        );
      }

      setFormOpen(false);
      setForm(EMPTY_FORM);
      setMembers([]);

      showFeedback(
        "success",
        data.message ||
          (editing
            ? "Permiso actualizado correctamente."
            : "Permiso creado correctamente.")
      );

      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo guardar el permiso."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePermission) {
      return;
    }

    setDeletingId(deletePermission.id);

    try {
      const response = await fetchUser(
        `/access-control/api/web/group-permissions/${deletePermission.id}/`,
        {
          method: "DELETE",
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok === false) {
        throw new Error(
          getApiMessage(
            data,
            "No se pudo eliminar el permiso."
          )
        );
      }

      setDeletePermission(null);

      showFeedback(
        "success",
        data.message || "Permiso eliminado correctamente."
      );

      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el permiso."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const selectedExcludedMembers = useMemo(() => {
    return members.filter((member) =>
      form.excluded_person_ids.includes(member.id)
    );
  }, [form.excluded_person_ids, members]);

  if (loading && !organization) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                gap: 1,
              }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: {
                    xs: "inline-flex",
                    md: "none",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#111111",
                    fontSize: {
                      xs: 26,
                      md: 32,
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Permisos por rol
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontWeight: 500,
                    marginTop: 0.5,
                  }}
                >
                  {permissions.length} permisos registrados
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={openCreate}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 44,
                borderBottom: "3px solid #b00020",
              }}
            >
              Nuevo permiso
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #e1e1e4",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                padding: {
                  xs: 2,
                  sm: 2.5,
                },
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Buscar por rol, acceso u horario"
              />
            </Box>

            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rol</TableCell>
                      <TableCell>Acceso</TableCell>
                      <TableCell>Horario</TableCell>
                      <TableCell>Regla</TableCell>
                      <TableCell>Excluidos</TableCell>
                      <TableCell>Vigencia</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedPermissions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Box
                            sx={{
                              minHeight: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>
                              No hay permisos por rol
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedPermissions.map((permission) => (
                        <TableRow key={permission.id} hover>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 800,
                              }}
                            >
                              {permission.group_name}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {permission.access_point_name}
                          </TableCell>

                          <TableCell>
                            {permission.schedule_name || "Sin horario"}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                permission.is_allowed
                                  ? "Permitir"
                                  : "Denegar"
                              }
                              sx={{
                                fontWeight: 800,
                                color: permission.is_allowed
                                  ? "#047857"
                                  : "#b00020",
                                backgroundColor: permission.is_allowed
                                  ? "#ecfdf5"
                                  : "#fff1f2",
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            {permission.exclusions?.filter(
                              (exclusion) => exclusion.is_active
                            ).length ?? 0}
                          </TableCell>

                          <TableCell>
                            <Typography sx={{ fontSize: 12 }}>
                              {formatDate(permission.valid_from)}
                            </Typography>

                            <Typography
                              sx={{
                                color: "#6b7280",
                                fontSize: 12,
                              }}
                            >
                              hasta {formatDate(permission.valid_until)}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  setDetailPermission(permission)
                                }
                              >
                                Ver
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openEdit(permission)}
                              >
                                Editar
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  setDeletePermission(permission)
                                }
                              >
                                Eliminar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              sx={{
                display: {
                  xs: "block",
                  md: "none",
                },
                padding: 2,
              }}
            >
              {paginatedPermissions.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    No hay permisos por rol
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {paginatedPermissions.map((permission) => (
                    <Box
                      key={permission.id}
                      sx={{
                        padding: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          marginBottom: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 800,
                            }}
                          >
                            {permission.group_name}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              fontSize: 13,
                            }}
                          >
                            {permission.access_point_name}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={
                            permission.is_allowed
                              ? "Permitir"
                              : "Denegar"
                          }
                          sx={{
                            flexShrink: 0,
                            fontWeight: 800,
                            color: permission.is_allowed
                              ? "#047857"
                              : "#b00020",
                            backgroundColor: permission.is_allowed
                              ? "#ecfdf5"
                              : "#fff1f2",
                          }}
                        />
                      </Box>

                      <Typography sx={{ fontSize: 13 }}>
                        <strong>Horario:</strong>{" "}
                        {permission.schedule_name || "Sin horario"}
                      </Typography>

                      <Typography sx={{ fontSize: 13, marginTop: 0.5 }}>
                        <strong>Excluidos:</strong>{" "}
                        {permission.exclusions?.filter(
                          (exclusion) => exclusion.is_active
                        ).length ?? 0}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          marginTop: 0.5,
                          marginBottom: 1.5,
                        }}
                      >
                        <strong>Vigencia:</strong>{" "}
                        {formatDate(permission.valid_from)} hasta{" "}
                        {formatDate(permission.valid_until)}
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setDetailPermission(permission)
                          }
                        >
                          Ver
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(permission)}
                        >
                          Editar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() =>
                            setDeletePermission(permission)
                          }
                          sx={{ gridColumn: "1 / -1" }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <TablePagination
              component="div"
              count={filteredPermissions.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas"
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {form.id ? "Editar permiso por rol" : "Nuevo permiso por rol"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ paddingTop: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>

              <Select
                value={form.group}
                label="Rol"
                onChange={handleGroupChange}
              >
                {groups
                  .filter(
                    (group) =>
                      group.is_active ||
                      group.id === Number(form.group)
                  )
                  .map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Acceso</InputLabel>

              <Select
                value={form.access_point}
                label="Acceso"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    access_point: event.target.value,
                  }))
                }
              >
                {accessPoints
                  .filter(
                    (accessPoint) =>
                      accessPoint.is_active ||
                      accessPoint.id === Number(form.access_point)
                  )
                  .map((accessPoint) => (
                    <MenuItem
                      key={accessPoint.id}
                      value={accessPoint.id}
                    >
                      {accessPoint.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Horario</InputLabel>

              <Select
                value={form.schedule}
                label="Horario"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    schedule: event.target.value,
                  }))
                }
              >
                <MenuItem value="">Sin horario</MenuItem>

                {schedules
                  .filter(
                    (schedule) =>
                      schedule.is_active ||
                      schedule.id === Number(form.schedule)
                  )
                  .map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                paddingX: 2,
                paddingY: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  marginBottom: 0.5,
                }}
              >
                Tipo de regla
              </Typography>

              <RadioGroup
                row
                value={form.is_allowed ? "allow" : "deny"}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    is_allowed: event.target.value === "allow",
                  }))
                }
              >
                <FormControlLabel
                  value="allow"
                  control={<Radio />}
                  label="Permitir acceso"
                />

                <FormControlLabel
                  value="deny"
                  control={<Radio />}
                  label="Denegar acceso"
                />
              </RadioGroup>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                type="date"
                label="Válido desde"
                value={form.valid_from}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    valid_from: event.target.value,
                  }))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                type="date"
                label="Válido hasta"
                value={form.valid_until}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    valid_until: event.target.value,
                  }))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  marginBottom: 1,
                }}
              >
                Usuarios excluidos de esta regla
              </Typography>

              {membersLoading ? (
                <Box
                  sx={{
                    minHeight: 90,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={members}
                  value={selectedExcludedMembers}
                  getOptionLabel={(option) =>
                    `${option.name} - ${option.identification}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disabled={!form.group}
                  onChange={(_, selectedMembers) =>
                    setForm((previous) => ({
                      ...previous,
                      excluded_person_ids: selectedMembers.map(
                        (member) => member.id
                      ),
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Excluir usuarios"
                      placeholder={
                        form.group
                          ? "Seleccionar usuarios"
                          : "Primero selecciona un rol"
                      }
                    />
                  )}
                />
              )}

              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: 12,
                  marginTop: 0.8,
                }}
              >
                Los usuarios seleccionados no heredarán esta regla del rol.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress
                size={20}
                sx={{ color: "#ffffff" }}
              />
            ) : form.id ? (
              "Guardar cambios"
            ) : (
              "Crear permiso"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detailPermission)}
        onClose={() => setDetailPermission(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Información del permiso</DialogTitle>

        <DialogContent>
          {detailPermission && (
            <Stack spacing={1.5} sx={{ paddingTop: 1 }}>
              {[
                ["Rol", detailPermission.group_name],
                ["Acceso", detailPermission.access_point_name],
                [
                  "Horario",
                  detailPermission.schedule_name || "Sin horario",
                ],
                [
                  "Regla",
                  detailPermission.is_allowed
                    ? "Permitir acceso"
                    : "Denegar acceso",
                ],
                [
                  "Válido desde",
                  formatDate(detailPermission.valid_from),
                ],
                [
                  "Válido hasta",
                  formatDate(detailPermission.valid_until),
                ],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    paddingBottom: 1.2,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginTop: 0.3,
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}

              <Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 1,
                  }}
                >
                  Usuarios excluidos
                </Typography>

                {!detailPermission.exclusions?.length ? (
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    Ninguno
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {detailPermission.exclusions
                      .filter((exclusion) => exclusion.is_active)
                      .map((exclusion) => (
                        <Box
                          key={exclusion.id}
                          sx={{
                            padding: 1.2,
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 800,
                            }}
                          >
                            {exclusion.person_name}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              fontSize: 12,
                            }}
                          >
                            {exclusion.person_identification}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailPermission(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletePermission)}
        onClose={() => {
          if (!deletingId) {
            setDeletePermission(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar permiso</DialogTitle>

        <DialogContent>
          <Typography>
            ¿Deseas eliminar el permiso de{" "}
            {deletePermission?.group_name} para{" "}
            {deletePermission?.access_point_name}?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeletePermission(null)}
            disabled={Boolean(deletingId)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={Boolean(deletingId)}
          >
            {deletingId ? (
              <CircularProgress
                size={20}
                sx={{ color: "#ffffff" }}
              />
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() =>
          setFeedback((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() =>
            setFeedback((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}